import React, { useEffect, useState, useCallback, useRef } from 'react';
import useLokiErrorMonitor from '../auth/hooks/useLokiErrorMonitor';
import { useSelector } from 'react-redux';
import ConfirmToastPopup from './ConfirmToastPopup';
import ToastPopupModal from './ToastPopupModal';
import ClearLogsConfirmPopup from './ClearLogsConfirmPopup';
import { getIconComponentWithSeverity } from '../../utils/toastIconMapping';
import type { RootState } from '@/rootreducer';

interface ErrorLog {
  timestamp: number;
  message: string;
  labels: Record<string, string>;
  severity: string;
  level?: string;
}

const ErrorToast: React.FC = () => {
  const LOKI_WS_URL = import.meta.env.VITE_LOKI_WS_URL || '';
  const LOOKBACK_MINUTES = Number(import.meta.env.VITE_LOKI_LOOKBACK_MINUTES) || 2880;
  const { selectedCustomerName, customerSites, selectedSite, FactoryPath, permittedCustomers } = useSelector((s: RootState) => s.userInfo);
  const selectedSiteId = customerSites.find((item: any) => item.name.toLowerCase() === selectedSite?.toLowerCase())?.id;

  // Get Loki preferences from customer and site
  const selectedCustomer = permittedCustomers.find((c) => c.customer === selectedCustomerName);
  const customerLoki = selectedCustomer?.preferences?.loki || '';

  const selectedSiteData = customerSites.find((item) => item.name.toLowerCase() === selectedSite?.toLowerCase());
  const siteLoki = selectedSiteData?.preferences?.loki || '';

  const hasSelectedSite = !!selectedSite;
  const lokiPreferencesValid = hasSelectedSite
    ? (!!customerLoki && !!siteLoki)  // Both customer and site Loki required when site is selected
    : !!customerLoki;  // Only customer Loki required for global logs


  const {
    errorLogs,
    // clearCommands,  // No longer needed with sequence ID approach
    isConnected,
    isMonitoring,
    isLoadingHistorical,
    startMonitoring,
    stopMonitoring,
    clearErrors,
  } = useLokiErrorMonitor(LOKI_WS_URL, {
    namespace: ["toast", "Toast", "TOAST"],  // Array of namespaces for toast system
    pollInterval: 5000,
    lookbackMinutes: LOOKBACK_MINUTES,  // Configured via env variable (default: 2880 = 2 days)
    debug: true,
    location: siteLoki,
    customer: customerLoki,
    enabled: lokiPreferencesValid
  });

  const [toasts, setToasts] = useState<Array<ErrorLog & { id: string; visible: boolean; parsedData?: any }>>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedToastForConfirm, setSelectedToastForConfirm] = useState<ErrorLog & { id: string; visible: boolean; parsedData?: any } | null>(null);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [buttonPosition, setButtonPosition] = useState({ top: 70, right: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [selectedToastIds, setSelectedToastIds] = useState<Set<string>>(new Set());
  const [showClearConfirmPopup, setShowClearConfirmPopup] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; top: number; right: number } | null>(null);
  const hasDraggedRef = useRef(false);
  const seenErrorsRef = useRef(new Set<string>());
  const isInitialLoadRef = useRef(true);
  const clearedSequenceIdsRef = useRef(new Set<string>()); // Track Clear commands


  // Track unique errors to prevent duplicates
  const getErrorId = useCallback((error: ErrorLog) => {
    return `${error.timestamp}-${error.message.substring(0, 50)}`;
  }, []);

  // Clear toasts when customer or site changes
  useEffect(() => {
   

    // Stop current monitoring
    stopMonitoring();
    clearErrors();

    // Clear component state
    setToasts([]);
    setBannerVisible(false);
    seenErrorsRef.current.clear();
    clearedSequenceIdsRef.current.clear(); // Clear the early-Clear tracking
    isInitialLoadRef.current = true; // Reset to fetch fresh historical data
    setSelectedToastIds(new Set()); // Clear selected toast IDs
  }, [selectedCustomerName, selectedSiteId, stopMonitoring, clearErrors]);

  // Sync selectedToastIds with current visible toasts
  useEffect(() => {
    const visibleToastIds = new Set(toasts.filter(t => t.visible).map(t => t.id));

    setSelectedToastIds(prev => {
      const updated = new Set<string>();
      prev.forEach(id => {
        // Only keep selected IDs that still exist in visible toasts
        if (visibleToastIds.has(id)) {
          updated.add(id);
        }
      });

      // Only update if there's a difference
      if (updated.size !== prev.size) {
    
        return updated;
      }
      return prev;
    });
  }, [toasts]);

  useEffect(() => {
    if (isLoadingHistorical) {
      return;
    }

    // Helper to parse toast message
    const parseToastData = (log: ErrorLog) => {
      try {
        return typeof log.message === 'string' ? JSON.parse(log.message) : log.message;
      } catch {
        return {};
      }
    };

    // INITIAL LOAD: Historical logs already processed by backend (active toasts only)
    if (isInitialLoadRef.current && errorLogs.length > 0) {
  

      // Verify backend filtering: Check for Set/Clear commands
      const setCommands = errorLogs.filter(log => {
        const data = parseToastData(log);
        const cmd = data.toast_command?.toLowerCase();
        return cmd === 'set';
      });
      const clearCommands = errorLogs.filter(log => {
        const data = parseToastData(log);
        const cmd = data.toast_command?.toLowerCase();
        return cmd === 'clear';
      });

  

      if (clearCommands.length > 0) {

      

        // Log Clear sequence IDs and timestamps
        clearCommands.forEach(clearLog => {
          const clearData = parseToastData(clearLog);
        
        });

        console.warn(' Applying frontend-side filtering as fallback...');
      }

      // Log Set sequence IDs and timestamps

      setCommands.forEach(setLog => {
        const setData = parseToastData(setLog);
  
      });

      // FRONTEND FALLBACK: Filter Set/Clear pairs (in case backend filtering fails)
      // Only keep Set commands that don't have a matching Clear command
      const activeSetCommands = setCommands.filter(setLog => {
        const setData = parseToastData(setLog);
        const setSequenceId = setData.sequence_id;

        // Check if there's a Clear command with same sequence_id
        // Note: Timestamp comparison removed - if Clear exists for this sequence_id, filter it out
        const hasCleared = clearCommands.some(clearLog => {
          const clearData = parseToastData(clearLog);
          const matches = clearData.sequence_id === setSequenceId;
       
          return matches;
        });

        return !hasCleared; // Keep only Set commands without matching Clear
      });

      if (activeSetCommands.length < setCommands.length) {
       //
      }

      const initialToasts = activeSetCommands.map(log => {
        const data = parseToastData(log);
        return {
          ...log,
          id: data.sequence_id || getErrorId(log),
          visible: true,
          parsedData: data,
          severity: data.severity || data.level || log.severity || log.level || 'ERROR'
        };
      });

   
      setToasts(initialToasts);
      setBannerVisible(initialToasts.length > 0);

      // Mark all as seen
      errorLogs.forEach(log => {
        const data = parseToastData(log);
        seenErrorsRef.current.add(data.sequence_id || getErrorId(log));
      });

      isInitialLoadRef.current = false;
      return;
    }

    // REAL-TIME: Process Set/Clear commands incrementally
    const newLogs = errorLogs.filter(log => {
      const data = parseToastData(log);
      // Use sequence_id + toast_command as unique ID (Clear messages have no message content)
      const uniqueId = `${data.sequence_id}-${data.toast_command}`;

      if (seenErrorsRef.current.has(uniqueId)) {
        return false;
      }
      seenErrorsRef.current.add(uniqueId);
      return true;
    });

    if (newLogs.length === 0) return;



    newLogs.forEach(log => {
      const data = parseToastData(log);
      const command = data.toast_command?.toLowerCase();

      if (command === 'set') {
     

        // Check if this toast was already cleared (Clear arrived before Set)
        if (clearedSequenceIdsRef.current.has(data.sequence_id)) {
        
          // Remove from tracking set since we've handled this pair
          clearedSequenceIdsRef.current.delete(data.sequence_id);
          return;
        }

        // Add new toast
   

        const newToast = {
          ...log,
          id: data.sequence_id,
          visible: true,
          parsedData: data,
          severity: data.severity || data.level || log.severity || log.level || 'ERROR'
        };

        setToasts(prev => {
          // Check if already exists
          if (prev.some(t => t.id === data.sequence_id)) {
         
            return prev;
          }
          return [newToast, ...prev].slice(0, 5);
        });
        setBannerVisible(true);

      } else if (command === 'clear') {
        

        // Track this Clear in case Set arrives later (out-of-order)
        clearedSequenceIdsRef.current.add(data.sequence_id);
    

        // Try to remove the toast if it already exists
        setToasts(prev => {
       
          const toastExists = prev.some(t => t.id === data.sequence_id);

          if (toastExists) {
            // Toast exists, remove it
            const updated = prev.filter(t => {
              const keep = t.id !== data.sequence_id;
            
              return keep;
            });
           

            if (updated.length === 0) {
           
              setBannerVisible(false);
            }
            return updated;
          } else {
            // Toast doesn't exist yet - Clear arrived before Set
           
            return prev;
          }
        });
      }
    });

  }, [errorLogs, isLoadingHistorical, getErrorId]);

  const displayToast = (message: any, isInPopup: boolean = false): React.ReactNode => {
    try {
      let parsedData: any = message;

      // If message is a string, try to parse it as JSON
      if (typeof message === 'string') {
        try {
          parsedData = JSON.parse(message);
        } catch {
          // Not JSON, use as-is
          parsedData = message;
        }
      }

      // If we have a parsed object with a message property, use that
      if (typeof parsedData === 'object' && parsedData !== null && parsedData.message) {
        if (isInPopup) {
          // In popup, show more details
          return (
            <div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {parsedData.message}
              </div>
              {parsedData.module && (
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                  Module: {parsedData.module}
                  {parsedData.function && ` → ${parsedData.function}()`}
                  {parsedData.line_number && ` [Line ${parsedData.line_number}]`}
                </div>
              )}
              {parsedData.timestamp && (
                <div style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic' }}>
                  Error timestamp: {new Date(parseFloat(parsedData.timestamp) * 1000).toLocaleString()}
                </div>
              )}
            </div>
          );
        } else {
          // In banner, show only the main message
          return parsedData.message;
        }
      }

      // If it's still a string, return it
      if (typeof parsedData === 'string') {
        return parsedData;
      }

      // Fallback: stringify the object
      return (
        <pre style={{
          margin: 0,
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          fontSize: 11
        }}>
          {JSON.stringify(parsedData, null, 2)}
        </pre>
      );
    } catch (e) {
      console.error('Error formatting message:', e);
      return 'Error displaying message';
    }
  };

  // Remove hidden toasts after fade animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.visible));
    }, 500);
    return () => clearTimeout(timer);
  }, [toasts]);

  // Start monitoring once WebSocket is connected
  useEffect(() => {
    if (isConnected && !isMonitoring) {
      startMonitoring({
        customer: customerLoki,
        location: siteLoki,
        namespace: ["toast", "Toast", "TOAST"],
        pollInterval: 5000,
        lookbackMinutes: LOOKBACK_MINUTES,
      });
    }
  }, [isConnected, isMonitoring, startMonitoring, selectedCustomerName, selectedSiteId]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev =>
      prev.map(t =>
        t.id === id ? { ...t, visible: false } : t
      )
    );
  }, []);

  // Handle checkbox toggle for toast selection
  const handleCheckboxToggle = useCallback((toastId: string) => {
    setSelectedToastIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(toastId)) {
        newSet.delete(toastId);
      } else {
        newSet.add(toastId);
      }
      return newSet;
    });
  }, []);

  // Send Loki Clear commands in batch for selected toasts
  const sendBatchLokiClearCommands = useCallback(async (selectedIds: Set<string>) => {
    try {
      const lokiPushUrl = import.meta.env.VITE_LOKI_PUSH_URL;
      if (!lokiPushUrl) {
        console.error('VITE_LOKI_PUSH_URL not configured in environment');
        return false;
      }

      // Get all selected toasts
      const selectedToasts = toasts.filter(t => selectedIds.has(t.id));
      if (selectedToasts.length === 0) {
        console.warn('No toasts found for selected IDs');
        return false;
      }

      // Get current time in nanoseconds
      const currentTimeNano = `${Date.now()}000000`;

      // Group toasts by stream labels (application, component, environment, namespace)
      const streamGroups = new Map<string, { stream: Record<string, string>; values: [string, string][] }>();

      selectedToasts.forEach(toast => {
        const data = toast.parsedData || {};

        // Extract labels from toast
        const application = toast.labels?.application || '';
        const component = toast.labels?.component || '';
        const environment = toast.labels?.environment || 'production';
        const namespace = toast.labels?.namespace || 'Toast';

        // Create a unique key for this stream combination
        const streamKey = `${application}|${component}|${environment}|${namespace}`;

        // Extract data from parsedData
        const sequenceId = data.sequence_id || toast.id;
        const level = data.level || toast.severity || 'ERROR';
        const customer = data.customer || selectedCustomerName;
        const location = data.location;

        // Build the message object
        const messageObj: any = {
          toast_command: 'Clear',
          sequence_id: sequenceId,
          level: level,
          timestamp: currentTimeNano
        };

        // Add customer if it exists
        if (customer) {
          messageObj.customer = customer;
        }

        // Add location if it exists
        if (location) {
          messageObj.location = location;
        }

        // Get or create the group for this stream
        if (!streamGroups.has(streamKey)) {
          streamGroups.set(streamKey, {
            stream: { application, component, environment, namespace },
            values: []
          });
        }

        // Add the message to the stream's values
        const streamGroup = streamGroups.get(streamKey);
        if (streamGroup) {
          streamGroup.values.push([
            currentTimeNano,
            JSON.stringify(messageObj)
          ]);
        }
      });

      // Build the payload with all streams
      const payload = {
        streams: Array.from(streamGroups.values())
      };

     
      const response = await fetch(`${lokiPushUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Loki API error: ${response.status} ${response.statusText}`);
      }

     
      return true;
    } catch (error) {
      console.error('Error sending batch Loki Clear commands:', error);
      return false;
    }
  }, [toasts, selectedCustomerName]);

  // Handle clear logs confirmation
  const handleClearLogsConfirmed = useCallback(async () => {
    if (selectedToastIds.size > 0) {
    
      const success = await sendBatchLokiClearCommands(selectedToastIds);
      if (success) {
        // Clear the selection after successful clear
        setSelectedToastIds(new Set());
      }
    }
  }, [selectedToastIds, sendBatchLokiClearCommands]);

  // Drag handlers for minimized button
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    hasDraggedRef.current = false; // Reset drag flag
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      top: buttonPosition.top,
      right: buttonPosition.right
    };
  }, [buttonPosition]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;

      const deltaX = dragStartRef.current.x - e.clientX;
      const deltaY = e.clientY - dragStartRef.current.y;

      // If mouse moved more than 5px, consider it a drag
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasDraggedRef.current = true;
      }

      setButtonPosition({
        top: Math.max(0, dragStartRef.current.top + deltaY),
        right: Math.max(0, dragStartRef.current.right + deltaX)
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const getSeverityColor = (severity: string, customColor?: string) => {
    // If custom color is provided, use it
    if (customColor) {
      return getColorSchemeFromCustom(customColor);
    }

    // Default severity-based colors
    switch (severity.toUpperCase()) {
      case 'CRITICAL':
      case 'FATAL':
        return { bg: '#fee2e2', border: '#fca5a5', text: '#7f1d1d', title: '#991b1b' };
      case 'ERROR':
        return { bg: '#fef2f2', border: '#fecaca', text: '#7f1d1d', title: '#b91c1c' };
      case 'WARN':
      case 'WARNING':
        return { bg: '#fef3c7', border: '#fcd34d', text: '#78350f', title: '#b45309' };
      case 'ASLEEP':
        return { bg: '#e5e7eb', border: '#9ca3af', text: '#1f2937', title: '#4b5563' };
      case 'DEBUG':
        return { bg: '#dbeafe', border: '#93c5fd', text: '#1e3a8a', title: '#1e40af' };
      default:
        return { bg: '#f0fdf4', border: '#bbf7d0', text: '#14532d', title: '#15803d' };
    }
  };

  // Generate color scheme from custom color
  const getColorSchemeFromCustom = (color: string) => {
    // Map common color names to color schemes
    const colorMap: Record<string, { bg: string; border: string; text: string; title: string }> = {
      'red': { bg: '#fee2e2', border: '#fca5a5', text: '#7f1d1d', title: '#991b1b' },
      'orange': { bg: '#ffedd5', border: '#fdba74', text: '#7c2d12', title: '#c2410c' },
      'yellow': { bg: '#fef3c7', border: '#fcd34d', text: '#78350f', title: '#b45309' },
      'green': { bg: '#dcfce7', border: '#86efac', text: '#14532d', title: '#15803d' },
      'blue': { bg: '#dbeafe', border: '#93c5fd', text: '#1e3a8a', title: '#1e40af' },
      'indigo': { bg: '#e0e7ff', border: '#a5b4fc', text: '#312e81', title: '#4338ca' },
      'purple': { bg: '#f3e8ff', border: '#d8b4fe', text: '#581c87', title: '#7e22ce' },
      'pink': { bg: '#fce7f3', border: '#f9a8d4', text: '#831843', title: '#be185d' },
      'gray': { bg: '#e5e7eb', border: '#9ca3af', text: '#1f2937', title: '#4b5563' },
      'cyan': { bg: '#cffafe', border: '#67e8f9', text: '#164e63', title: '#0891b2' },
      'teal': { bg: '#ccfbf1', border: '#5eead4', text: '#134e4a', title: '#0f766e' },
      'brown': { bg: '#fef3c7', border: '#d97706', text: '#451a03', title: '#92400e' },
      'lime': { bg: '#ecfccb', border: '#bef264', text: '#365314', title: '#4d7c0f' },
      'emerald': { bg: '#d1fae5', border: '#6ee7b7', text: '#064e3b', title: '#047857' },
      'sky': { bg: '#e0f2fe', border: '#7dd3fc', text: '#0c4a6e', title: '#0369a1' },
      'violet': { bg: '#ede9fe', border: '#c4b5fd', text: '#4c1d95', title: '#6d28d9' },
      'fuchsia': { bg: '#fae8ff', border: '#f0abfc', text: '#701a75', title: '#a21caf' },
      'rose': { bg: '#ffe4e6', border: '#fda4af', text: '#881337', title: '#be123c' },
      'amber': { bg: '#fef3c7', border: '#fcd34d', text: '#78350f', title: '#d97706' },
      'slate': { bg: '#f1f5f9', border: '#cbd5e1', text: '#1e293b', title: '#334155' },
    };

    // Check if it's a named color
    const lowerColor = color.toLowerCase();
    if (colorMap[lowerColor]) {
      return colorMap[lowerColor];
    }

    // If it's a hex color or CSS color, create a basic scheme
    // For simplicity, use the color as title and create lighter variants
    return {
      bg: `${color}15`, // 15% opacity
      border: `${color}60`, // 60% opacity
      text: color,
      title: color
    };
  };

  const getBannerColors = (severity: string, customColor?: string) => {
    // If custom color is provided, create gradients from it
    if (customColor) {
      const colorScheme = getColorSchemeFromCustom(customColor);
      // Use the title color for gradients (it's the most saturated)
      const baseColor = colorScheme.title;
      const darkColor = colorScheme.text;
      return {
        gradient: `linear-gradient(90deg, ${baseColor} 0%, ${darkColor} 100%)`,
        hoverGradient: `linear-gradient(90deg, ${darkColor} 0%, ${darkColor} 100%)`
      };
    }

    const upperSeverity = severity?.toUpperCase() || '';

    // Critical/Error - Red
    if (upperSeverity === 'CRITICAL' || upperSeverity === 'FATAL' || upperSeverity === 'ERROR') {
      return {
        gradient: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)',
        hoverGradient: 'linear-gradient(90deg, #b91c1c 0%, #991b1b 100%)'
      };
    }

    // Warning - Orange
    if (upperSeverity === 'WARN' || upperSeverity === 'WARNING') {
      return {
        gradient: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
        hoverGradient: 'linear-gradient(90deg, #d97706 0%, #b45309 100%)'
      };
    }

    // Asleep - Gray
    if (upperSeverity === 'ASLEEP') {
      return {
        gradient: 'linear-gradient(90deg, #6b7280 0%, #4b5563 100%)',
        hoverGradient: 'linear-gradient(90deg, #4b5563 0%, #374151 100%)'
      };
    }

    // Debug/Set - Blue
    if (upperSeverity === 'DEBUG' || upperSeverity === 'SET') {
      return {
        gradient: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
        hoverGradient: 'linear-gradient(90deg, #2563eb 0%, #1e40af 100%)'
      };
    }

    // Default - Green
    return {
      gradient: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
      hoverGradient: 'linear-gradient(90deg, #16a34a 0%, #15803d 100%)'
    };
  };

  const visibleToasts = toasts.filter(t => t.visible);
  const totalCount = visibleToasts.length;

  // Count errors and warnings separately
  const errorCount = visibleToasts.filter(t => {
    const severity = t.severity?.toUpperCase();
    return severity === 'ERROR' || severity === 'CRITICAL' || severity === 'FATAL';
  }).length;

  const warningCount = visibleToasts.filter(t => {
    const severity = t.severity?.toUpperCase();
    return severity === 'WARN' || severity === 'WARNING';
  }).length;

  // Helper function to get severity priority (lower number = higher priority)
  const getSeverityPriority = (severity: string): number => {
    const upperSeverity = severity?.toUpperCase() || '';
    if (upperSeverity === 'CRITICAL' || upperSeverity === 'FATAL') return 1;
    if (upperSeverity === 'ERROR') return 2;
    if (upperSeverity === 'WARN' || upperSeverity === 'WARNING') return 3;
    if (upperSeverity === 'ASLEEP') return 4;
    return 5; // DEBUG, INFO, or others
  };

  // Get the highest priority toast (Critical > Error > Warning > others)
  const highestPriorityToast = visibleToasts.length > 0
    ? visibleToasts.reduce((highest, current) => {
        const highestPriority = getSeverityPriority(highest.severity);
        const currentPriority = getSeverityPriority(current.severity);
        return currentPriority < highestPriority ? current : highest;
      })
    : null;

  const customColor = highestPriorityToast?.parsedData?.color;
  const customIconName = highestPriorityToast?.parsedData?.icon || highestPriorityToast?.parsedData?.Icon || highestPriorityToast?.parsedData?.ICON;
  const bannerColors = getBannerColors(highestPriorityToast?.severity || 'ERROR', customColor);
  const BannerIcon = getIconComponentWithSeverity(customIconName, highestPriorityToast?.severity);




  if (toasts.length === 0) return null;

  return (
    <>
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>

      {/* Minimized residue button - shows when banner is closed but alerts remain */}
      {!bannerVisible && totalCount > 0 && (
        <div
          onClick={() => !isDragging && !hasDraggedRef.current && setBannerVisible(true)}
          onMouseDown={handleMouseDown}
          style={{
            position: 'fixed',
            top: buttonPosition.top,
            right: buttonPosition.right,
            background: bannerColors.gradient,
            borderRadius: 6,
            padding: '6px 10px',
            cursor: isDragging ? 'grabbing' : 'grab',
            boxShadow: isDragging ? '0 8px 20px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.2)',
            transition: isDragging ? 'none' : 'all 0.2s ease',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            userSelect: 'none'
          }}
          onMouseEnter={(e) => {
            if (!isDragging) {
              e.currentTarget.style.background = bannerColors.hoverGradient;
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragging) {
              e.currentTarget.style.background = bannerColors.gradient;
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          <div style={{
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '50%',
            padding: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            color: 'white'
          }}>
            {BannerIcon && <BannerIcon />}
          </div>
          <div style={{
            color: 'white',
            fontSize: 12,
            fontWeight: 700,
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
          }}>
            {totalCount}
          </div>
        </div>
      )}

      {/* Full-width error/warning banner */}
      {bannerVisible && totalCount > 0 && (
        <div
          onClick={() => setShowPopup(true)}
          style={{
            background: bannerColors.gradient,
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.2s ease',
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            marginBottom: '16px',
            borderRadius: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = bannerColors.hoverGradient;
            e.currentTarget.style.boxShadow = '0 8px 16px -4px rgba(0, 0, 0, 0.25)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = bannerColors.gradient;
            e.currentTarget.style.boxShadow = '0 4px 12px -2px rgba(0, 0, 0, 0.15)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.25)',
              borderRadius: '50%',
              padding: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
              color: 'white'
            }}>
              {BannerIcon && <BannerIcon />}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{
                color: 'white',
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 4,
                fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
                letterSpacing: '0.3px'
              }}>
                {errorCount > 0 && `${errorCount} Error${errorCount !== 1 ? 's' : ''}`}
                {errorCount > 0 && warningCount > 0 && ' • '}
                {warningCount > 0 && `${warningCount} Warning${warningCount !== 1 ? 's' : ''}`}
              </div>
              <div style={{
                color: 'rgba(255, 255, 255, 0.92)',
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '650px',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                lineHeight: 1.4,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                <span style={{
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  padding: '2px 6px',
                  borderRadius: 3
                }}>
                  {highestPriorityToast?.severity?.toUpperCase() === 'WARN' || highestPriorityToast?.severity?.toUpperCase() === 'WARNING' ? 'WARNING' : highestPriorityToast?.severity?.toUpperCase() || 'ERROR'}
                </span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayToast(highestPriorityToast?.message) || 'Unknown error'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              color: 'white',
              fontSize: 12,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              letterSpacing: '0.2px',
              padding: '3px 8px',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.15)',
              transition: 'background 0.2s ease'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
            >
              View Details
              <span style={{ fontSize: 16, marginTop: 1 }}>›</span>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setBannerVisible(false);
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.25)',
                border: 'none',
                borderRadius: 4,
                padding: 6,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                color: 'white',
                fontSize: 16,
                lineHeight: 1,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Error popup modal */}

      <ToastPopupModal
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        toasts={toasts}
        selectedToastIds={selectedToastIds}
        handleCheckboxToggle={handleCheckboxToggle}
        onClearLogsClick={() => setShowClearConfirmPopup(true)}
        setSelectedToastIds={setSelectedToastIds}
        displayToast={displayToast}
        getSeverityColor={getSeverityColor}
        setSelectedToastForConfirm={setSelectedToastForConfirm}
        setShowConfirmPopup={setShowConfirmPopup}
        selectedCustomerName={selectedCustomerName}
      />

      {/* Clear Logs Confirmation Popup */}
      <ClearLogsConfirmPopup
        isOpen={showClearConfirmPopup}
        onClose={() => setShowClearConfirmPopup(false)}
        onConfirmClear={handleClearLogsConfirmed}
        selectedCount={selectedToastIds.size}
      />

      {/* Confirm popup - Create Ticket */}
      <ConfirmToastPopup
        isOpen={showConfirmPopup}
        onClose={() => {
          setShowConfirmPopup(false);
          setSelectedToastForConfirm(null);
        }}
        toast={selectedToastForConfirm}
        selectedCustomerName={selectedCustomerName}
        selectedSite={selectedSite}
      />
    </>
  );
};

export default ErrorToast;